<template>
  <div class="puntos">
    <div class="puntos__cliente">
      <v-icon color="primary" class="mr-2">mdi-account-star-outline</v-icon>
      <span class="puntos__nombre">{{ nombreCliente }}</span>
    </div>

    <div class="puntos__datos">
      <div class="puntos__dato puntos__dato--principal">
        <span>Puntos del cliente</span>
        <strong>{{ disponibles }}</strong>
        <small>Equivalen a {{ $filters.currency(disponibles * valorPorPunto, 0) }}</small>
      </div>
      <div class="puntos__dato">
        <span>Cada punto descuenta</span>
        <strong>{{ $filters.currency(valorPorPunto, 0) }}</strong>
      </div>
      <div class="puntos__dato">
        <span>Mínimo para canjear</span>
        <strong>{{ minimo }} {{ minimo === 1 ? "punto" : "puntos" }}</strong>
      </div>
    </div>

    <v-alert v-if="getCustomer().points_expire" dense text color="warning" icon="mdi-clock-alert-outline" class="mb-3">
      {{ getCustomer().points_expire }} puntos vencen el {{ $filters.date(getCustomer().points_expiration_date) }}.
    </v-alert>

    <template v-if="usados">
      <div class="puntos__aplicado">
        <v-icon color="success" large class="mr-3">mdi-check-circle-outline</v-icon>
        <div class="puntos__aplicado-texto">
          <strong>{{ points }} puntos aplicados</strong>
          <span>Descuento de {{ $filters.currency(descuentoDe(points), 0) }} en esta venta</span>
        </div>
        <v-btn outlined color="error" @click="quitar()">Quitar puntos</v-btn>
      </div>
    </template>

    <template v-else>
      <v-alert v-if="disponibles < minimo" dense text color="info" icon="mdi-information-outline" class="mb-3">
        El cliente aún no llega al mínimo de {{ minimo }} puntos para canjear.
      </v-alert>
      <v-alert v-else-if="maximo < minimo" dense text color="info" icon="mdi-information-outline" class="mb-3">
        El saldo de esta venta no alcanza para canjear el mínimo de {{ minimo }} puntos.
      </v-alert>

      <template v-else>
        <div class="puntos__entrada">
          <ng-number-keyboard v-model="points" label="Puntos a canjear" outlined :filled="false" dense hide-details class="puntos__campo" @input="ajustar()"></ng-number-keyboard>
          <div class="puntos__atajos">
            <v-chip label outlined color="primary" @click="poner(minimo)">Mínimo · {{ minimo }}</v-chip>
            <v-chip label outlined color="primary" @click="poner(maximo)">Máximo · {{ maximo }}</v-chip>
          </div>
        </div>
        <div class="puntos__resumen" :class="{ 'puntos__resumen--listo': puedeAplicar }">
          <span>Descuento</span>
          <strong>{{ $filters.currency(descuentoDe(points), 0) }}</strong>
          <small v-if="Number(points) > 0 && Number(points) < minimo" class="error--text">Faltan {{ minimo - Number(points) }} para el mínimo</small>
          <small v-else>Hasta {{ maximo }} puntos en esta venta</small>
        </div>
        <div v-if="requiereCodigo && !verificado" class="puntos__codigo">
          <template v-if="!codigoEnviado">
            <span>Para canjear, el cliente confirma con un código que se le envía a su correo.</span>
            <v-btn color="primary" depressed large :loading="enviando" :disabled="!puedeAplicar" @click="enviarCodigo()">
              <v-icon left>mdi-email-fast-outline</v-icon>Enviar código
            </v-btn>
          </template>
          <template v-else>
            <span>Código enviado a <strong>{{ correo }}</strong>. Pídeselo al cliente.</span>
            <div class="puntos__codigo-fila">
              <ng-number-keyboard v-model="codigo" label="Código de 6 dígitos" outlined :filled="false" dense hide-details class="puntos__campo"></ng-number-keyboard>
              <v-btn color="primary" depressed large :loading="verificando" :disabled="String(codigo).length < 6" @click="verificarCodigo()">Verificar</v-btn>
              <v-btn text color="primary" :disabled="enviando" @click="enviarCodigo()">Reenviar</v-btn>
            </div>
          </template>
          <small v-if="errorCodigo" class="error--text">{{ errorCodigo }}</small>
        </div>
        <template v-else>
          <div v-if="requiereCodigo" class="puntos__verificado"><v-icon small color="success" class="mr-1">mdi-shield-check-outline</v-icon>Cliente confirmado con su código</div>
          <v-btn color="primary" depressed x-large block :disabled="!puedeAplicar" @click="aplicar()">
            <v-icon left>mdi-check-outline</v-icon>Aplicar puntos
          </v-btn>
        </template>
      </template>
    </template>

    <p v-if="usados" class="puntos__nota"><v-icon small>mdi-information-outline</v-icon> Una venta en la que se canjean puntos no suma puntos nuevos.</p>
    <p v-else-if="ganaria > 0" class="puntos__nota"><v-icon small>mdi-star-outline</v-icon> Si no canjea, esta compra le suma {{ ganaria }} {{ ganaria === 1 ? "punto" : "puntos" }}.</p>
  </div>
</template>

<script>
/** Canje de puntos en el pago. El servidor revalida todo (puntos, valor del descuento, mínimo, cliente); aquí sólo se guía al cajero. */
export default {
  props: ["order"],
  data() {
    return {
      points: 0,
      usados: false,
      codigoEnviado: false,
      verificado: false,
      codigo: "",
      correo: "",
      errorCodigo: "",
      enviando: false,
      verificando: false,
    };
  },
  computed: {
    nombreCliente() {
      const c = this.getCustomer();
      return [c.first_name, c.last_name].filter(Boolean).join(" ") || "Cliente";
    },
    disponibles() {
      return Number(this.getCustomer().current_points) || 0;
    },
    valorPorPunto() {
      return Number(this.$store.getters.getGeneralValue("gen_others_discount_value_per_point")) || 0;
    },
    minimo() {
      return Number(this.$store.getters.getGeneralValue("gen_others_minimum_number_of_redeemable_points")) || 0;
    },
    // lo que falta por cubrir de la venta, contando el descuento de puntos que ya tenga aplicado
    porCubrir() {
      const orden = this.$pos.getCurrentOrder();
      return orden.getBalance() + (Number(orden.getDiscountPoints()) || 0);
    },
    maximo() {
      if (this.valorPorPunto <= 0 || this.porCubrir <= 0) {
        return 0;
      }
      return Math.max(0, Math.min(this.disponibles, Math.floor(this.porCubrir / this.valorPorPunto)));
    },
    requiereCodigo() {
      return this.$store.getters.getGeneralValue("gen_others_points_require_code") === true;
    },
    puedeAplicar() {
      const p = Number(this.points);
      return p > 0 && p >= this.minimo && p <= this.maximo;
    },
    // lo que sumaría la venta si no se canjea (el servidor no da puntos a una venta con canje)
    ganaria() {
      const porPunto = Number(this.$store.getters.getGeneralValue("gen_others_minimum_ammount_per_point")) || 0;
      return porPunto > 0 ? Math.floor(this.$pos.getCurrentOrder().getTotalToPay() / porPunto) : 0;
    },
  },
  mounted() {
    this.points = Number(this.order.points_used) || 0;
    this.usados = this.points > 0;
  },
  methods: {
    getCustomer() {
      return this.order.data_customer ?? {};
    },
    descuentoDe(puntos) {
      return (Number(puntos) || 0) * this.valorPorPunto;
    },
    poner(puntos) {
      this.points = puntos;
    },
    ajustar() {
      this.$nextTick(() => {
        const p = Number(this.points) || 0;
        if (p < 0) {
          this.points = 0;
        } else if (p > this.maximo) {
          this.points = this.maximo;
        }
      });
    },
    datosCliente() {
      const c = this.getCustomer();
      return { customer_id: c.id, identification: c.identification };
    },
    // el servidor es quien manda y revisa el código; aquí sólo se pide y se muestra el resultado
    async enviarCodigo() {
      this.enviando = true;
      this.errorCodigo = "";
      try {
        const res = await this.$http.post("api/v1/pos/points/send-code", this.datosCliente(), false, false, false);
        this.correo = res.email;
        this.codigo = "";
        this.codigoEnviado = true;
      } catch (e) {
        this.errorCodigo = this.mensajeDe(e, "No se pudo enviar el código. Revisa la conexión.");
      }
      this.enviando = false;
    },
    async verificarCodigo() {
      this.verificando = true;
      this.errorCodigo = "";
      try {
        await this.$http.post("api/v1/pos/points/verify-code", { ...this.datosCliente(), code: String(this.codigo) }, false, false, false);
        this.verificado = true;
      } catch (e) {
        this.errorCodigo = this.mensajeDe(e, "No se pudo verificar el código.");
      }
      this.verificando = false;
    },
    mensajeDe(e, porDefecto) {
      const r = e && e.response && e.response.data;
      return (r && (r.message || r.msg)) || porDefecto;
    },
    aplicar() {
      if (!this.puedeAplicar) {
        return;
      }
      const orden = this.$pos.getCurrentOrder();
      const monto = this.descuentoDe(this.points);
      orden.setPointsUsed(Number(this.points));
      orden.setDiscountPoints(monto);
      orden.setTotalGlobalDiscounts(monto);
      this.usados = true;
    },
    quitar() {
      const orden = this.$pos.getCurrentOrder();
      this.points = 0;
      this.usados = false;
      orden.setPointsUsed(0);
      orden.setDiscountPoints(0);
      orden.setTotalGlobalDiscounts(0);
    },
  },
};
</script>

<style scoped>
.puntos {
  padding: 16px 20px;
}
.puntos__cliente {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 18px;
  font-weight: 700;
  color: var(--pos-azul-marino, #0b1f33);
}
.puntos__datos {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  gap: 10px;
  margin-bottom: 14px;
}
.puntos__dato {
  display: flex;
  flex-direction: column;
  padding: 10px 14px;
  border-radius: 8px;
  background: #f5f6f8;
}
.puntos__dato--principal {
  background: #e8f4fd;
  border: 1px solid #b6dcf5;
}
.puntos__dato span,
.puntos__dato small {
  font-size: 12px;
  color: #5f6368;
}
.puntos__dato strong {
  font-size: 20px;
  color: var(--pos-azul-marino, #0b1f33);
}
.puntos__dato--principal strong {
  font-size: 28px;
  color: #1976d2;
}
.puntos__entrada {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}
.puntos__campo {
  flex: 1 1 220px;
  max-width: 280px;
}
.puntos__atajos {
  display: flex;
  gap: 8px;
}
.puntos__resumen {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding: 10px 14px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: #f5f6f8;
}
.puntos__resumen--listo {
  background: #eaf7ee;
  border: 1px solid #b7dfb9;
}
.puntos__resumen strong {
  font-size: 24px;
  color: #2e7d32;
}
.puntos__resumen small {
  color: #5f6368;
}
.puntos__aplicado {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 8px;
  background: #eaf7ee;
  border: 1px solid #b7dfb9;
}
.puntos__aplicado-texto {
  display: flex;
  flex-direction: column;
  flex: 1 1 200px;
}
.puntos__aplicado-texto span {
  color: #5f6368;
}
.puntos__codigo {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fff8e1;
  border: 1px solid #ffe08a;
}
.puntos__codigo-fila {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.puntos__verificado {
  margin-bottom: 8px;
  font-size: 13px;
  color: #2e7d32;
}
.puntos__nota {
  margin: 12px 0 0;
  font-size: 13px;
  color: #5f6368;
}
@media (max-width: 600px) {
  .puntos__datos {
    grid-template-columns: 1fr 1fr;
  }
  .puntos__dato--principal {
    grid-column: span 2;
  }
}
</style>
